import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Braces,
  Zap,
  BadgeIndianRupee,
  ArrowRight,
  Check,
  Copy,
  LayoutDashboard,
  Smartphone,
  CopyPlus,
  ShieldCheck,
  Gauge,
  Webhook,
  Menu,
  Phone,
} from "lucide-react";

import { landingContent } from "../data/landingContent";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "../components/ui/sheet";

import { toast } from "../components/ui/sonner";

const ICONS = {
  Braces,
  Zap,
  BadgeIndianRupee,
  LayoutDashboard,
  Smartphone,
  CopyPlus,
};

function useRevealOnScroll() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const els = Array.from(root.querySelectorAll("[data-reveal='true']"));
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) e.target.setAttribute("data-revealed", "true");
        }
      },
      { threshold: 0.15 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return rootRef;
}

const SectionHeading = ({ kicker, title, description, align = "left" }) => {
  const alignCls = align === "center" ? "text-center mx-auto" : "";
  return (
    <div className={"max-w-2xl " + alignCls}>
      {kicker ? (
        <div className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground">
          {kicker}
        </div>
      ) : null}
      <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
        {title}
      </h2>
      {description ? (
        <p className="mt-3 text-base md:text-[1.05rem] leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
};

const MessageFlowIllustration = () => {
  return (
    <div className="relative h-[340px] md:h-[420px] w-full" aria-hidden="true">
      {/* soft blobs */}
      <div className="pointer-events-none absolute -left-10 top-10 h-56 w-56 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(13,148,136,0.22),transparent_60%)] blur-2xl" />
      <div className="pointer-events-none absolute -right-12 bottom-2 h-72 w-72 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(245,158,11,0.18),transparent_60%)] blur-2xl" />

      <div className="absolute inset-0 grid place-items-center">
        <div className="relative w-full max-w-[560px]">
          {/* glass panel */}
          <div className="werelay-glass werelay-float rounded-2xl border border-white/40 p-5 md:p-6 shadow-[0_20px_70px_rgba(2,6,23,0.10)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-white/70 grid place-items-center border border-black/5">
                  <img
                    src={landingContent.brand.logoMark}
                    alt=""
                    className="h-5 w-5 object-contain"
                    loading="lazy"
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Message flow
                  </div>
                  <div className="text-xs text-muted-foreground">
                    API → Queue → WhatsApp → Webhook
                  </div>
                </div>
              </div>
              <Badge className="bg-white/70 text-foreground border border-black/5">
                live
              </Badge>
            </div>

            <div className="mt-5 grid grid-cols-12 gap-4 items-center">
              <Node label="Your app" icon={Gauge} colSpan="col-span-5" />
              <Flow colSpan="col-span-2" />
              <Node label="WeRelay queue" icon={ShieldCheck} colSpan="col-span-5" />

              <div className="col-span-12">
                <Separator className="bg-black/10" />
              </div>

              <Node label="WhatsApp" icon={Zap} colSpan="col-span-5" />
              <Flow colSpan="col-span-2" reverse />
              <Node label="Your webhook" icon={Webhook} colSpan="col-span-5" />
            </div>

            {/* moving message dot */}
            <div className="relative mt-6 h-2 overflow-hidden rounded-full bg-black/5">
              <div className="werelay-dot absolute top-0 h-full w-10 rounded-full bg-[linear-gradient(90deg,rgba(13,148,136,0.0),rgba(13,148,136,0.75),rgba(245,158,11,0.75),rgba(245,158,11,0.0))]" />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <MiniBubble side="left" />
              <MiniBubble side="right" />
            </div>
          </div>

          <div className="pointer-events-none absolute -inset-3 -z-10 rounded-[28px] bg-[radial-gradient(circle_at_20%_20%,rgba(13,148,136,0.18),transparent_55%),radial-gradient(circle_at_80%_70%,rgba(245,158,11,0.16),transparent_55%)] blur-xl" />
        </div>
      </div>
    </div>
  );
};

const Node = ({ label, icon: Icon, colSpan }) => {
  return (
    <div className={colSpan}>
      <div className="rounded-2xl border border-black/5 bg-white/60 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-white grid place-items-center border border-black/5">
            <Icon className="h-4 w-4 text-[#0f766e]" />
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">
              {label}
            </div>
            <div className="text-xs text-muted-foreground">
              {label === "WeRelay queue" ? "retries • ordering" : ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Flow = ({ colSpan, reverse = false }) => {
  return (
    <div className={colSpan + " hidden md:block"}>
      <div className="relative h-10">
        <div
          className={
            "absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-black/10"
          }
        />
        <div
          className={
            "werelay-pulse absolute top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-[#0f766e] " +
            (reverse ? "right-0" : "left-0")
          }
        />
      </div>
    </div>
  );
};

const MiniBubble = ({ side }) => {
  const align = side === "left" ? "justify-start" : "justify-end";
  const bg = side === "left" ? "bg-white/70" : "bg-white/55";
  return (
    <div className={"flex " + align}>
      <div className={"max-w-[240px] rounded-2xl border border-black/5 px-4 py-3 " + bg}>
        <div className="text-xs font-semibold text-foreground">
          {side === "left" ? "POST /messages" : "delivery.webhook"}
        </div>
        <div className="mt-1 text-[12px] leading-snug text-muted-foreground">
          {side === "left"
            ? "Queued • retry-safe"
            : "delivered • message_id: …"}
        </div>
      </div>
    </div>
  );
};

const GlassCard = ({ children, className = "" }) => {
  return (
    <Card
      className={
        "werelay-glass werelay-card group relative overflow-hidden border-white/40 shadow-[0_18px_55px_rgba(2,6,23,0.08)] " +
        className
      }
    >
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <div className="absolute -left-24 -top-24 h-60 w-60 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(13,148,136,0.18),transparent_60%)] blur-2xl" />
        <div className="absolute -right-28 -bottom-24 h-72 w-72 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(245,158,11,0.14),transparent_60%)] blur-2xl" />
      </div>
      <div className="relative p-6">{children}</div>
    </Card>
  );
};

export default function LandingPage() {
  const rootRef = useRevealOnScroll();
  const [copied, setCopied] = useState(false);

  const codeLines = useMemo(
    () => landingContent.apiPreview.code.split("\n"),
    []
  );

  const copyWithFallback = async (text) => {
    // Prefer modern clipboard API
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // continue to fallback
    }

    // Fallback: hidden textarea + execCommand
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      ta.style.left = "-9999px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const onCopy = async () => {
    const ok = await copyWithFallback(landingContent.apiPreview.code);
    if (ok) {
      setCopied(true);
      toast.success("Copied to clipboard");
      window.setTimeout(() => setCopied(false), 1200);
      return;
    }
    toast.error("Could not copy. Please copy manually.");
  };

  return (
    <div ref={rootRef} className="min-h-screen bg-[#f7f7f8]">
      <SiteHeader />

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_15%,rgba(13,148,136,0.10),transparent_55%),radial-gradient(circle_at_70%_25%,rgba(245,158,11,0.10),transparent_55%)]" />
            <div className="werelay-noise absolute inset-0 opacity-[0.12]" />
          </div>

          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-12 md:pb-16">
            <div className="grid items-center gap-12 md:grid-cols-2">
              <div data-reveal="true" className="werelay-reveal">
                <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
                  {landingContent.hero.headline}
                </h1>
                <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                  {landingContent.hero.subheadline}
                </p>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  {landingContent.hero.description}
                </p>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Button
                    asChild
                    className="werelay-cta h-11 px-5 rounded-xl"
                  >
                    <a href={landingContent.links.login}>
                      {landingContent.hero.primaryCta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="h-11 px-5 rounded-xl bg-white/60 border-black/10 hover:bg-white"
                  >
                    <a href={landingContent.links.docs}>
                      {landingContent.hero.secondaryCta}
                    </a>
                  </Button>
                </div>
              </div>

              <div data-reveal="true" className="werelay-reveal md:pl-4">
                <MessageFlowIllustration />
              </div>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="py-6 md:py-8 border-b border-black/10 bg-white/40">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
              {landingContent.trustStrip.items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <Check className="h-4 w-4 text-[#0f766e] flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY */}
        <section id="why" className="py-14 md:py-18">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div data-reveal="true" className="werelay-reveal">
              <SectionHeading
                title={landingContent.why.title}
                description={landingContent.why.description}
              />
            </div>
          </div>
        </section>

        {/* CAPABILITIES */}
        <section id="capabilities" className="py-14 md:py-18 bg-white/55">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div data-reveal="true" className="werelay-reveal">
              <SectionHeading
                title={landingContent.capabilities.title}
                description={landingContent.capabilities.description}
              />
            </div>

            <div className="mt-9 grid gap-5 md:grid-cols-2">
              {landingContent.capabilities.items.map((item) => (
                <div key={item.title} data-reveal="true" className="werelay-reveal">
                  <GlassCard className="h-full">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </GlassCard>
                </div>
              ))}
            </div>

            {landingContent.capabilities.note && (
              <div className="mt-6 text-sm text-muted-foreground text-center">
                {landingContent.capabilities.note}
              </div>
            )}
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how" className="py-14 md:py-18">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div data-reveal="true" className="werelay-reveal">
              <SectionHeading
                title={landingContent.howItWorks.title}
                description={landingContent.howItWorks.description}
              />
            </div>

            <div className="mt-10 grid gap-4">
              {landingContent.howItWorks.steps.map((s, idx) => (
                <div key={s.title} data-reveal="true" className="werelay-reveal">
                  <GlassCard>
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-xl bg-[#0f766e]/10 border border-[#0f766e]/15 grid place-items-center text-sm font-semibold text-[#0f766e]">
                          {String(idx + 1).padStart(2, "0")}
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-foreground">
                            {s.title}
                          </h3>
                          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                            {s.description}
                          </p>
                        </div>
                      </div>
                      <div className="md:pl-4">
                        <Badge className="bg-white/70 text-foreground border border-black/10">
                          {idx === 0
                            ? "setup"
                            : idx === 1
                            ? "request"
                            : idx === 2
                            ? "delivery"
                            : "events"}
                        </Badge>
                      </div>
                    </div>
                  </GlassCard>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* API PREVIEW */}
        <section id="api" className="py-14 md:py-18 bg-white/55">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 md:grid-cols-2 md:items-start">
              <div data-reveal="true" className="werelay-reveal">
                <SectionHeading
                  title={landingContent.apiPreview.title}
                  description={landingContent.apiPreview.description}
                />
              </div>

              <div data-reveal="true" className="werelay-reveal">
                <Card className="overflow-hidden border-black/10 shadow-[0_18px_55px_rgba(2,6,23,0.08)]">
                  <div className="flex items-center justify-between gap-3 bg-white/70 px-4 py-3 border-b border-black/10">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-black text-white border-black">
                        {landingContent.apiPreview.codeLanguage}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        send a message
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onCopy}
                      className="h-9 rounded-xl bg-white/60 border-black/10 hover:bg-white"
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>

                  <div className="bg-[#0b0c0f]">
                    <pre className="p-4 md:p-5 text-[12.5px] leading-relaxed text-[#e7e7e8] overflow-auto">
                      <code>
                        {codeLines.map((line, i) => (
                          <div key={i} className="whitespace-pre-wrap">
                            {line}
                          </div>
                        ))}
                      </code>
                    </pre>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* RELIABILITY */}
        <section id="reliability" className="py-14 md:py-18">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div data-reveal="true" className="werelay-reveal">
              <SectionHeading
                title={landingContent.reliability.title}
                description={landingContent.reliability.description}
              />
            </div>

            <div className="mt-9 grid gap-4 md:grid-cols-2">
              {landingContent.reliability.points.map((point, idx) => (
                <div key={idx} data-reveal="true" className="werelay-reveal">
                  <GlassCard className="h-full">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/70 border border-black/10 flex-shrink-0">
                        <Check className="h-3.5 w-3.5 text-[#0f766e]" />
                      </div>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {point}
                      </p>
                    </div>
                  </GlassCard>
                </div>
              ))}
            </div>

            {landingContent.reliability.note && (
              <div className="mt-6 text-sm text-muted-foreground text-center">
                {landingContent.reliability.note}
              </div>
            )}
          </div>
        </section>

        {/* SECURITY */}
        <section id="security" className="py-14 md:py-18 bg-white/55">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div data-reveal="true" className="werelay-reveal">
              <SectionHeading
                title={landingContent.security.title}
                description={landingContent.security.description}
              />
            </div>

            <div className="mt-9 grid gap-4 md:grid-cols-2">
              {landingContent.security.points.map((point, idx) => (
                <div key={idx} data-reveal="true" className="werelay-reveal">
                  <GlassCard className="h-full">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/70 border border-black/10 flex-shrink-0">
                        <ShieldCheck className="h-3.5 w-3.5 text-[#0f766e]" />
                      </div>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {point}
                      </p>
                    </div>
                  </GlassCard>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ROADMAP */}
        <section id="roadmap" className="py-14 md:py-18 bg-white/55">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div data-reveal="true" className="werelay-reveal">
              <SectionHeading
                title={landingContent.roadmap.title}
              />
            </div>

            <div className="mt-9 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {landingContent.roadmap.items.map((item, idx) => (
                <div key={idx} data-reveal="true" className="werelay-reveal">
                  <GlassCard className="h-full">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/70 border border-black/10 flex-shrink-0">
                        <Check className="h-3.5 w-3.5 text-[#0f766e]" />
                      </div>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {item}
                      </p>
                    </div>
                  </GlassCard>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="py-14 md:py-18">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div data-reveal="true" className="werelay-reveal">
              <SectionHeading
                title={landingContent.pricing.title}
                description={landingContent.pricing.description}
              />
            </div>

            <div className="mt-9 grid gap-4 md:grid-cols-3">
              {landingContent.pricing.points.map((point, idx) => (
                <div key={idx} data-reveal="true" className="werelay-reveal">
                  <GlassCard className="h-full">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/70 border border-black/10 flex-shrink-0">
                        <Check className="h-3.5 w-3.5 text-[#0f766e]" />
                      </div>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {point}
                      </p>
                    </div>
                  </GlassCard>
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <Button asChild className="werelay-cta h-11 px-6 rounded-xl">
                <a href={landingContent.links.sales}>
                  {landingContent.pricing.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-14 md:py-18">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div data-reveal="true" className="werelay-reveal">
              <SectionHeading
                title={landingContent.faq.title}
              />
            </div>

            <div data-reveal="true" className="werelay-reveal mt-8">
              <Card className="werelay-glass border-white/40 overflow-hidden">
                <div className="p-2">
                  <Accordion type="single" collapsible className="w-full">
                    {landingContent.faq.items.map((it, idx) => (
                      <AccordionItem
                        key={it.q}
                        value={`item-${idx}`}
                        className="border-black/10"
                      >
                        <AccordionTrigger className="px-4 py-4 text-left text-foreground hover:no-underline">
                          {it.q}
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 text-muted-foreground">
                          {it.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={
        "sticky top-0 z-50 border-b transition-colors duration-300 " +
        (scrolled
          ? "border-black/10 bg-white/65 backdrop-blur-[18px]"
          : "border-transparent bg-transparent")
      }
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <a href="#" className="flex items-center gap-2" aria-label="Home">
            <img
              src={landingContent.brand.logoMark}
              alt="WeRelay"
              className="h-7 w-auto"
              loading="eager"
            />
            <span className="text-lg font-semibold text-foreground">WeRelay App</span>
          </a>

          <nav className="hidden md:flex items-center gap-1">
            <HeaderLink href="#why" label="Why" />
            <HeaderLink href="#capabilities" label="Capabilities" />
            <HeaderLink href="#how" label="How" />
            <HeaderLink href="#api" label="API" />
            <HeaderLink href="#reliability" label="Reliability" />
            <HeaderLink href="#faq" label="FAQ" />
          </nav>

          <div className="flex items-center gap-2">
            {/* Mobile menu */}
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 rounded-xl bg-white/60 border-black/10 hover:bg-white"
                    aria-label="Open menu"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="p-0">
                  <SheetHeader className="px-6 pt-6 pb-4">
                    <SheetTitle className="flex items-center gap-2">
                      <img
                        src={landingContent.brand.logoMark}
                        alt="WeRelay"
                        className="h-7 w-auto"
                        loading="lazy"
                      />
                      <span className="text-lg font-semibold text-foreground">WeRelay App</span>
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                      Navigation menu
                    </SheetDescription>
                  </SheetHeader>

                  <div className="px-6 pb-6">
                    <div className="grid gap-1">
                      <MobileNavLink href="#why" label="Why" />
                      <MobileNavLink href="#capabilities" label="Capabilities" />
                      <MobileNavLink href="#how" label="How" />
                      <MobileNavLink href="#api" label="API" />
                      <MobileNavLink href="#reliability" label="Reliability" />
                      <MobileNavLink href="#faq" label="FAQ" />
                    </div>

                    <div className="mt-6 grid gap-2">
                      <Button
                        asChild
                        variant="outline"
                        className="h-11 rounded-xl bg-white/70 border-black/10 hover:bg-white"
                      >
                        <a href={landingContent.links.docs}>Docs</a>
                      </Button>
                      <Button asChild className="werelay-cta h-11 rounded-xl">
                        <a href={landingContent.links.login}>
                          {landingContent.hero.primaryCta}
                        </a>
                      </Button>
                    </div>

                    <div className="mt-6 text-xs text-muted-foreground">
                      Built for fast onboarding and reliable delivery.
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <Button
              asChild
              variant="outline"
              className="hidden sm:inline-flex h-10 rounded-xl bg-white/60 border-black/10 hover:bg-white"
            >
              <a href={landingContent.links.docs}>Docs</a>
            </Button>
            <Button asChild className="werelay-cta h-10 rounded-xl px-4">
              <a href={landingContent.links.login}>
                {landingContent.hero.primaryCta}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

function HeaderLink({ href, label }) {
  return (
    <a
      href={href}
      data-header-nav="true"
      className="rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
    >
      {label}
    </a>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-black/10 bg-[#f7f7f8]">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-lg font-semibold text-foreground">
              {landingContent.brand.name}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {landingContent.footer.tagline}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6 sm:grid-cols-2">
            <FooterCol
              title="Product"
              links={landingContent.footer.productLinks.map((link) => ({
                label: link.label,
                href: landingContent.links[link.href] || `#${link.href}`,
              }))}
            />
            <FooterCol
              title="Company"
              links={landingContent.footer.companyLinks.map((link) => ({
                label: link.label,
                href: landingContent.links[link.href] || `#${link.href}`,
              }))}
            />
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-black/10 pt-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <img
                className="h-5 w-5 rounded"
                src={landingContent.brand.logoMark}
                alt="WeRelay"
              />
              <span className="text-sm text-foreground font-medium">WeRelay</span>
              <span className="text-xs text-muted-foreground">by WeIncept</span>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">+91-8595714205</span>
            </div>
            <span className="text-xs text-muted-foreground">Built with ❤️ in India</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {landingContent.footer.tagline2}
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-sm font-semibold text-foreground">{title}</div>
      <ul className="mt-3 space-y-2">
        {links.map((l) => (
          <li key={l.label}>
            <a
              href={l.href}
              className="text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MobileNavLink({ href, label }) {
  return (
    <SheetClose asChild>
      <a
        href={href}
        data-mobile-nav="true"
        className="rounded-xl px-3 py-3 text-base text-foreground/90 transition-colors duration-200 hover:bg-black/5"
      >
        {label}
      </a>
    </SheetClose>
  );
}
